/**
 * Data Plan Dialog — mirror Access popup `frmMatchingPSMainSubFormDataPlan`
 * (file frontend/plan/access_all/form_frmMatchingPSMainSubFormDataPlan.txt).
 *
 * Read-only grid xem 3 nguồn báo cáo qua 1 cbFilter combo:
 *   - Match Date - P Grade   → v_match_report_direct, match_date / p_grade  (Direct cols)
 *   - Match Date - S Grade   → v_match_report_direct, match_date / s_grade  (Direct cols)
 *   - Latest Delivery - P Grade → v_match_report_stockp, p_time / p_grade   (StockP cols)
 *   - Shipment From - S Grade  → v_match_report_stocks, s_time / s_grade    (StockS cols)
 *
 * 3 mode button (DIRECT / P-INSTRUCTION / S-INSTRUCTION) chỉ giới hạn tập
 * cbFilter option khả dụng + set default — đúng VBA btnDirect_Click /
 * btnPStock_Click / btnSStock_Click. Không có CRUD ở dialog này (Direct
 * insert/delete đi qua DirectPSDialog riêng).
 *
 * Default date range = fiscal year hiện tại (Oct 1 → Sep 30), giống Form_Load
 * Access. category mặc định trống = không lọc grade.
 */
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined'
import CloseIcon from '@mui/icons-material/Close'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'

import BrandState from '@/components/BrandState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'

import {
  type DoByMatch,
  useDeleteDirectPsPlanMutation,
  useDeleteDoFromPlanMutation,
  useDeleteStockPPlanMutation,
  useDeleteStockSPlanMutation,
  useGetDataPlanRowsQuery,
  useGetMatchCategoriesQuery,
  useLazyGetDosByMatchQuery,
} from '../../../store/api/matchingPsApi'
import type { DataPlanFilter } from '../../../store/api/matchingPsApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { titleCaseName } from '../../../utils/text'
import ConfirmDialog from '../../../components/ConfirmDialog'
import CreateDoFromPlanDialog, {
  ACCENT_DO,
  type DoDraftSeed,
  type EditDoRow,
} from './CreateDoFromPlanDialog'
import { useMatchingFilterPref, type DataPlanFilterCtx } from './matchingFilterPrefs'
import {
  matchingFilterAutocompleteSx,
  matchingRangeDateFieldSx,
} from './matchingFilterUi'
import {
  HIDE_ON_MOBILE_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../../theme/responsive'

/* ---------- formatters (đồng nhất với DirectPSDialog) --------------------- */

const ACCESS_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

function fmtDate(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

function fmtDateTime(value: unknown): string {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

function fmtNumber(value: unknown, zeroBlank = false): string {
  if (value == null || value === '') return ''
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return String(value)
  // Access Format `#,###` (vd match_kg): số 0 → blank (khác `#,##0` ra "0").
  if (zeroBlank && n === 0) return ''
  const txt = Math.abs(Math.round(n)).toLocaleString('en-US')
  return n < 0 ? `(${txt})` : txt
}

/**
 * Access ConditionalFormat trên control `p_side` / `s_side`
 * (form_frmMatchingPSMainSubFormDirect): chữ đỏ `#ED1C24` khi side còn pending
 * (`p_plan_pending <> 0` / `s_plan_pending <> 0`). Operator là `<>` (acNotEqual),
 * KHÔNG phải `> 0`.
 */
function hasPending(value: unknown): boolean {
  if (value == null || value === '') return false
  const n = Number(value)
  return Number.isFinite(n) && n !== 0
}

// CF cột P/S Pending: pending < 0 → chữ đỏ #ED1C24 + bold (acLessThan, value 0).
function isNegPending(value: unknown): boolean {
  if (value == null || value === '') return false
  const n = Number(value)
  return Number.isFinite(n) && n < 0
}

function cellValue(row: Record<string, unknown>, col: Col): string {
  const v = row[col.key]
  if (v == null || v === '') return ''
  if (col.kind === 'date') return fmtDate(v)
  if (col.kind === 'datetime') return fmtDateTime(v)
  if (col.kind === 'number') return fmtNumber(v, col.zeroBlank)
  if (col.key === 'cpName' || col.key === 'pName' || col.key === 'sName') return titleCaseName(v)
  return String(v)
}

/* ---------- defaults ------------------------------------------------------ */

type DateRange = { from: string; to: string }

function defaultFiscalRange(now: Date = new Date()): DateRange {
  const y = now.getFullYear()
  const inQ4 = now.getMonth() >= 9
  const startYear = inQ4 ? y : y - 1
  const endYear = inQ4 ? y + 1 : y
  return { from: `${startYear}-10-01`, to: `${endYear}-09-30` }
}

/* ---------- column defs --------------------------------------------------- */

type ColKind = 'text' | 'date' | 'datetime' | 'number'

interface Col {
  key: string
  label: string
  width: number
  kind?: ColKind
  align?: 'left' | 'right'
  zeroBlank?: boolean // Access Format `#,###` → số 0 hiển thị blank
  headerTip?: string // Tooltip giải thích Conditional Format (highlight đỏ)
}

const TIP_SIDE = 'Red when this side still has pending quantity (plan pending ≠ 0).'
const TIP_PENDING = 'Quantity not yet planned. Red when negative (over-matched).'

// Mirror form_frmMatchingPSMainSubFormDirect_all — đúng thứ tự cột datasheet:
// ID, P Contract, P Supplier, P Quality, P Pending, MatchDate, MatchKG,
// S Contract, S Buyer, S Quality, S Pending, Note, P Direct, P InstructBL,
// P InstructNT, P Grade, P Quantity, S Direct, S InstructBL, S InstructNT,
// S Grade, S Quantity, Date Updated, Person Updated.
const DIRECT_COLS: Col[] = [
  { key: 'matchId', label: 'ID', width: 50 },
  { key: 'doLst', label: 'DO List', width: 96 },
  { key: 'doDone', label: 'Executed?', width: 70 },
  { key: 'pSide', label: 'P Contract', width: 96, headerTip: TIP_SIDE },
  { key: 'pName', label: 'P Supplier', width: 126 },
  { key: 'pQuality', label: 'P Quality', width: 88 },
  { key: 'pPlanPending', label: 'P Pending', width: 88, kind: 'number', align: 'right', headerTip: TIP_PENDING },
  { key: 'matchDate', label: 'MatchDate', width: 92, kind: 'date' },
  { key: 'matchKg', label: 'MatchKG', width: 84, kind: 'number', align: 'right', zeroBlank: true },
  { key: 'sSide', label: 'S Contract', width: 96, headerTip: TIP_SIDE },
  { key: 'sName', label: 'S Buyer', width: 126 },
  { key: 'sQuality', label: 'S Quality', width: 88 },
  { key: 'sPlanPending', label: 'S Pending', width: 88, kind: 'number', align: 'right', headerTip: TIP_PENDING },
  { key: 'note', label: 'Note', width: 150 },
  { key: 'pDirectS', label: 'P Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'pGrade', label: 'P Grade', width: 78 },
  { key: 'pQuantity', label: 'P Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'sDirectP', label: 'S Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'sInstructBaoloc', label: 'S InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'sInstructNhontrach', label: 'S InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'sGrade', label: 'S Grade', width: 78 },
  { key: 'sQuantity', label: 'S Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', width: 116, kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated', width: 150 },
]

// Display order theo chị Đẹp (2026-05-27) — table-style §10.8. ID left+raw
// (match_id Long, no Format); InstructKG zero-blank (Access `#,###`).
const STOCKP_COLS: Col[] = [
  { key: 'matchId', label: 'ID', width: 50 },
  { key: 'factoryName', label: 'StockSide', width: 116 },
  { key: 'matchDate', label: 'InstructDate', width: 110, kind: 'date' },
  { key: 'matchKg', label: 'InstructKG', width: 96, kind: 'number', align: 'right', zeroBlank: true },
  { key: 'pSide', label: 'P Contract', width: 96, headerTip: TIP_SIDE },
  { key: 'pName', label: 'P Supplier', width: 126 },
  { key: 'pQuality', label: 'P Quality', width: 88 },
  { key: 'note', label: 'Note', width: 150 },
  { key: 'pPlanPending', label: 'P Pending', width: 88, kind: 'number', align: 'right', headerTip: TIP_PENDING },
  { key: 'pDirectS', label: 'P Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'pInstructBaoloc', label: 'P InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'pInstructNhontrach', label: 'P InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'pGrade', label: 'P Grade', width: 78 },
  { key: 'pQuantity', label: 'P Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', width: 116, kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated', width: 150 },
]

// Mirror form_frmMatchingPSMainSubFormStockS_all — thứ tự cột + format condition
// verify từ doc (2026-05-27, theo yêu cầu chị Đẹp):
//   ID, StockSide, InstructDate, InstructKG, S Contract, S Buyer, S Quality, Note,
//   S Pending, S Direct, S InstructBL, S InstructNT, S Grade, S Quantity,
//   Date Updated, Person Updated.
// Access Format/TextAlign per control:
//   - match_id        TextAlign=1 (left), no Format  → left + raw (KHÔNG number-right)
//   - match_date      Format=DD-MMM-YYYY, left        → kind:'date'
//   - match_kg        Format=`#,###`, right           → number right + zeroBlank (0 → blank)
//   - s_plan_pending/s_direct_p/s_instruct_baoloc/s_instruct_nhontrach/s_quantity
//                     Format=`#,##0`, right           → number right (0 → "0")
//   - s_side          CF: chữ đỏ #ED1C24 khi [s_plan_pending]<>0 (áp ở itemContent)
//   - còn lại (s_name/s_quality/note/s_grade/factory_name/person_updated) left, raw
//   - date_updated    Text 255 (BE %Y-%m-%d %H:%M:%S) → kind:'datetime'
const STOCKS_COLS: Col[] = [
  { key: 'matchId', label: 'ID', width: 50 },
  { key: 'doLst', label: 'DO List', width: 96 },
  { key: 'doDone', label: 'Executed?', width: 70 },
  { key: 'factoryName', label: 'StockSide', width: 116 },
  { key: 'matchDate', label: 'InstructDate', width: 110, kind: 'date' },
  { key: 'matchKg', label: 'InstructKG', width: 96, kind: 'number', align: 'right', zeroBlank: true },
  { key: 'sSide', label: 'S Contract', width: 96, headerTip: TIP_SIDE },
  { key: 'sName', label: 'S Buyer', width: 126 },
  { key: 'sQuality', label: 'S Quality', width: 88 },
  { key: 'note', label: 'Note', width: 150 },
  { key: 'sPlanPending', label: 'S Pending', width: 88, kind: 'number', align: 'right', headerTip: TIP_PENDING },
  { key: 'sDirectP', label: 'S Direct', width: 84, kind: 'number', align: 'right' },
  { key: 'sInstructBaoloc', label: 'S InstructBL', width: 106, kind: 'number', align: 'right' },
  { key: 'sInstructNhontrach', label: 'S InstructNT', width: 106, kind: 'number', align: 'right' },
  { key: 'sGrade', label: 'S Grade', width: 78 },
  { key: 'sQuantity', label: 'S Quantity', width: 94, kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date Updated', width: 116, kind: 'datetime' },
  { key: 'personUpdated', label: 'Person Updated', width: 150 },
]

/* ---------- mode + filter mapping ----------------------------------------- */

type Mode = 'direct' | 'pstock' | 'sstock'

interface FilterOption {
  value: DataPlanFilter
  label: string
  dateLabel: string
  gradeLabel: string
  columns: Col[]
  // Schema discriminator → key cho column-visibility + column-widths (mỗi schema
  // có tập cột riêng nên persist riêng).
  schema: 'direct' | 'pstock' | 'sstock'
}

const FILTER_OPTIONS: Record<DataPlanFilter, FilterOption> = {
  match_date_p: {
    value: 'match_date_p',
    label: 'Match Date - P Grade',
    dateLabel: 'Match Date',
    gradeLabel: 'P Grade',
    columns: DIRECT_COLS,
    schema: 'direct',
  },
  match_date_s: {
    value: 'match_date_s',
    label: 'Match Date - S Grade',
    dateLabel: 'Match Date',
    gradeLabel: 'S Grade',
    columns: DIRECT_COLS,
    schema: 'direct',
  },
  latest_delivery_p: {
    value: 'latest_delivery_p',
    label: 'Latest Delivery - P Grade',
    dateLabel: 'Latest Delivery',
    gradeLabel: 'P Grade',
    columns: STOCKP_COLS,
    schema: 'pstock',
  },
  shipment_from_s: {
    value: 'shipment_from_s',
    label: 'Shipment From - S Grade',
    dateLabel: 'Shipment From',
    gradeLabel: 'S Grade',
    columns: STOCKS_COLS,
    schema: 'sstock',
  },
}

// Mỗi mode button mở 1 tập filter option + 1 default value (mirror VBA).
const MODE_CONFIG: Record<
  Mode,
  { label: string; filters: DataPlanFilter[]; defaultFilter: DataPlanFilter; color: string }
> = {
  direct: {
    label: 'DIRECT P - S',
    filters: ['match_date_p', 'match_date_s', 'latest_delivery_p', 'shipment_from_s'],
    defaultFilter: 'match_date_p',
    color: '#1e88e5',
  },
  pstock: {
    label: 'P - INSTRUCTION',
    filters: ['match_date_p', 'latest_delivery_p'],
    defaultFilter: 'latest_delivery_p',
    color: '#1e88e5',
  },
  sstock: {
    label: 'S - INSTRUCTION',
    filters: ['match_date_s', 'shipment_from_s'],
    defaultFilter: 'shipment_from_s',
    color: '#fb8c00',
  },
}

/* ---------- props --------------------------------------------------------- */

export interface DataPlanDialogProps {
  open: boolean
  onClose: () => void
  canEdit?: boolean
}

export default function DataPlanDialog({ open, onClose, canEdit = false }: DataPlanDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      // xs: MUI paper margin 32px + 90vw/95vh vượt 100vw/100vh → tràn viewport;
      // dvh (không phải vh) để trừ URL bar iOS Safari.
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 16px)', sm: '90vw' },
          maxWidth: { xs: 'calc(100vw - 16px)', sm: '90vw' },
          height: { xs: 'calc(100dvh - 16px)', sm: '95vh' },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: 'calc(100% - 32px)' },
          m: { xs: 1, sm: 2 },
        },
      }}
    >
      {open ? <DataPlanDialogBody onClose={onClose} canEdit={canEdit} /> : null}
    </Dialog>
  )
}

/* ---------- body ---------------------------------------------------------- */

function DataPlanDialogBody({ onClose, canEdit }: { onClose: () => void; canEdit: boolean }) {
  const fiscalDefault = useMemo(() => defaultFiscalRange(), [])
  const [mode, setMode] = useState<Mode>('direct')
  const [filter, setFilter] = useState<DataPlanFilter>('match_date_p')
  const [range, setRange] = useState<DateRange>(fiscalDefault)
  const [category, setCategory] = useState('')

  // Edit vs DO (chỉ mode direct) — chọn 1 dòng match → mở form sửa DO đã book.
  const snack = useSnackbar()
  // Multi-select: tick nhiều dòng. Delete Plan xoá tất cả dòng tick; Edit vs DO
  // yêu cầu đúng 1 dòng (sửa DO của 1 match).
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const toggleSelect = useCallback((mid: number) => {
    if (!Number.isFinite(mid)) return
    setSelectedIds((prev) =>
      prev.includes(mid) ? prev.filter((x) => x !== mid) : [...prev, mid],
    )
  }, [])
  const [doEditOpen, setDoEditOpen] = useState(false)
  const [doEditSeeds, setDoEditSeeds] = useState<DoDraftSeed[]>([])
  const [doEditRows, setDoEditRows] = useState<EditDoRow[]>([])
  const [fetchDosByMatch] = useLazyGetDosByMatchQuery()
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deletePlan, { isLoading: deleting }] = useDeleteDirectPsPlanMutation()
  const [deleteStockSPlan, { isLoading: deletingStockS }] = useDeleteStockSPlanMutation()
  const [deleteStockPPlan, { isLoading: deletingStockP }] = useDeleteStockPPlanMutation()
  const [deleteDoRow, { isLoading: deletingDos }] = useDeleteDoFromPlanMutation()
  const [planDos, setPlanDos] = useState<DoByMatch[]>([])

  // Persist filter (mode + cbFilter + date range + grade) qua user_preferences
  // (skill /user-logaction), context 'data-plan'. Query gate trên `hydrated` để
  // không fetch với default trước khi prefs về. Dialog re-mount mỗi lần mở →
  // hydrate lại từ prefs.
  const filterPref = useMatchingFilterPref<DataPlanFilterCtx>('data-plan')
  const { patch: patchFilter } = filterPref
  // Ref-latch để hydrate đúng 1 lần — set ref trước setState (đồng pattern
  // PurchaseContractPage) nên không trip rule react-hooks/set-state-in-effect.
  const hydratedRef = useRef(false)
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => {
    if (hydratedRef.current || filterPref.isLoading) return
    hydratedRef.current = true
    const v = filterPref.value
    const m: Mode = v.mode === 'pstock' || v.mode === 'sstock' ? v.mode : 'direct'
    const allowed = MODE_CONFIG[m].filters
    const f = (allowed as string[]).includes(v.filter)
      ? (v.filter as DataPlanFilter)
      : MODE_CONFIG[m].defaultFilter
    // eslint-disable-next-line react-hooks/set-state-in-effect -- hydrate-once filter từ prefs (ref-latched)
    setMode(m)
    setFilter(f)
    setRange({ from: v.dateFrom || fiscalDefault.from, to: v.dateTo || fiscalDefault.to })
    setCategory(v.category ?? '')
    setHydrated(true)
  }, [filterPref, fiscalDefault])

  // Switch mode → reset filter về default của mode đồng bộ trong cùng 1 render.
  // useEffect tách 2 render → frame trung gian Select nhận value không có trong
  // option list → MUI warning. Mirror VBA: btnDirect_Click set cbFilter ngay.
  const changeMode = useCallback(
    (next: Mode) => {
      const def = MODE_CONFIG[next].defaultFilter
      setMode(next)
      setFilter(def)
      setSelectedIds([])
      void patchFilter({ mode: next, filter: def })
    },
    [patchFilter],
  )
  const handleFilter = useCallback(
    (v: DataPlanFilter) => {
      setFilter(v)
      void patchFilter({ filter: v })
    },
    [patchFilter],
  )
  const handleRange = useCallback(
    (next: DateRange) => {
      setRange(next)
      void patchFilter({ dateFrom: next.from || null, dateTo: next.to || null })
    },
    [patchFilter],
  )
  const handleCategory = useCallback(
    (v: string) => {
      setCategory(v)
      void patchFilter({ category: v })
    },
    [patchFilter],
  )

  const opt = FILTER_OPTIONS[filter]
  const availableFilters = MODE_CONFIG[mode].filters

  const categoryOptionsQuery = useGetMatchCategoriesQuery()
  const categoryOptions = categoryOptionsQuery.data ?? []

  const rowsQuery = useGetDataPlanRowsQuery(
    {
      filter,
      startDate: range.from || undefined,
      endDate: range.to || undefined,
      category: category || undefined,
    },
    { skip: !hydrated },
  )
  const rows = useMemo(
    () => (rowsQuery.data ?? []) as Record<string, unknown>[],
    [rowsQuery.data],
  )

  const handleReload = () => {
    void rowsQuery.refetch()
  }

  /* ───────── table-style: search / per-column / sort / pagination ───────── */
  const schema = opt.schema
  const columns = opt.columns

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    searchPopover.close()
  }

  // Column visibility + reorder + widths — persist per-schema qua registry
  // matching-ps (giống StockSalesDataGrid). Không cần baseline migration: subKey
  // mới fallback về `allColumns` order (= thứ tự Access đã set ở trên).
  const widthsKey = `matching.data_plan.${schema}`
  const { getWidth, startResize } = useColumnWidths(widthsKey)
  const columnsCtrl = usePreferenceColumns<Col>({
    scope: 'matching-ps',
    prefKey: 'columns',
    subKey: `data-plan-${schema}`,
    allColumns: columns,
    widthsTableKey: widthsKey,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [rows, searchTerm, columns, columnFilters.filters],
  )
  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, columns),
    [filteredRows, sortState, columns],
  )
  const pagedRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  // DO áp cho mode Direct (v_match_report_direct, 'direct') và S-Instruction
  // (v_match_report_stocks, 'instruct'). pstock (P-Instruction) KHÔNG tạo DO.
  const doEditableMode = mode === 'direct' || mode === 'sstock'
  // Delete Plan áp cho cả 3 chế độ (Direct / P-Instruction / S-Instruction);
  // riêng Edit vs DO chỉ có ở mode gắn DO (doEditableMode). P-Instruction xoá
  // plan qua deleteStockPPlan và KHÔNG có DO để xoá kèm.
  const deletableMode = doEditableMode || mode === 'pstock'
  const editMatchType = mode === 'sstock' ? 'instruct' : 'direct'
  const editScenario: 'direct' | 'instruct' = mode === 'sstock' ? 'instruct' : 'direct'

  // Edit vs DO — mở form sửa DO của các match đã tick. Tick nhiều dòng → mỗi
  // match 1 khối DO riêng trong cùng cửa sổ (seeds[] + editRows gắn planId).
  const handleEditVsDo = async () => {
    if (!doEditableMode) return
    if (selectedIds.length === 0) {
      snack.warning('Tick at least one row to use Edit vs DO.')
      return
    }
    const seeds: DoDraftSeed[] = []
    const editRows: EditDoRow[] = []
    try {
      for (const id of selectedIds) {
        const row = rows.find((r) => Number(r.matchId) === id)
        if (!row) continue
        const matchKg = Number(row.matchKg) || 0
        let seed: DoDraftSeed
        if (mode === 'sstock') {
          const wh = String(row.factoryName ?? '')
          seed = {
            saleNo: String(row.sSide ?? ''),
            buyer: String(row.sName ?? ''),
            toWh: wh,
            doQty: matchKg,
            planRef: wh,
            planRefSub: 'TLC warehouse',
            planKg: matchKg,
            matchType: 'instruct',
            matchId: id,
          }
        } else {
          const supplier = String(row.pName ?? '')
          seed = {
            saleNo: String(row.sSide ?? ''),
            buyer: String(row.sName ?? ''),
            toWh: supplier,
            doQty: matchKg,
            planRef: String(row.pSide ?? ''),
            planRefSub: supplier ? `Shipper: ${supplier}` : '',
            planKg: matchKg,
            matchType: 'direct',
            matchId: id,
          }
        }
        const planIdx = seeds.length
        seeds.push(seed)
        try {
          const dos = await fetchDosByMatch({ matchId: id, matchType: editMatchType }).unwrap()
          for (const d of dos) {
            editRows.push({
              doId: d.deliveryOrderScsId,
              doNo: d.doNo ?? '',
              toWh: d.fromWarehouse ?? '',
              doQty: Number(d.doQty) || 0,
              note: d.note ?? '',
              planId: planIdx,
            })
          }
        } catch {
          /* match chưa lấy được DO → vẫn hiện khối để add DO mới */
        }
      }
      if (seeds.length === 0) {
        snack.warning('Selected rows not found.')
        return
      }
      setDoEditSeeds(seeds)
      setDoEditRows(editRows)
      setDoEditOpen(true)
    } catch {
      snack.error('Failed to load DO list.')
    }
  }

  // Delete Plan (direct + sstock + pstock) — soft-delete match; nếu có DO thì
  // xoá DO trước. P-Instruction (pstock) không có DO → bỏ qua bước fetch DO.
  const handleDelete = async () => {
    if (!canEdit) {
      snack.error("You don't have permission for this action.")
      return
    }
    if (selectedIds.length === 0) {
      snack.warning('Tick at least one row, then click Delete Plan.')
      return
    }
    if (doEditableMode) {
      const all: DoByMatch[] = []
      for (const id of selectedIds) {
        try {
          const dos = await fetchDosByMatch({ matchId: id, matchType: editMatchType }).unwrap()
          all.push(...dos)
        } catch {
          /* bỏ qua match không lấy được DO — vẫn cho xoá plan */
        }
      }
      setPlanDos(all)
    } else {
      setPlanDos([])
    }
    setConfirmDeleteOpen(true)
  }
  const confirmDelete = async () => {
    if (selectedIds.length === 0) return
    const errMsg = (err: unknown) =>
      (err as { data?: { detail?: string } })?.data?.detail ??
      (err as { message?: string })?.message ??
      'Delete failed'
    // 1) Xoá hết DO đã book (gộp mọi match đã tick) TRƯỚC. Clear planDos ngay sau
    //    khi xong để nếu bấm lại (retry) KHÔNG cố xoá DO đã biến mất.
    try {
      for (const d of planDos) {
        await deleteDoRow({ doId: d.deliveryOrderScsId }).unwrap()
      }
    } catch (err: unknown) {
      setPlanDos([]) // buộc handleDelete re-fetch DO còn lại ở lần thử sau
      snack.error(errMsg(err))
      return
    }
    setPlanDos([])
    // 2) Soft-delete từng Plan độc lập — 1 dòng lỗi không chặn các dòng còn lại;
    //    chỉ giữ lại các id lỗi trong selection để retry.
    const failed: number[] = []
    for (const id of selectedIds) {
      try {
        if (mode === 'sstock') await deleteStockSPlan({ matchId: id }).unwrap()
        else if (mode === 'pstock') await deleteStockPPlan({ matchId: id }).unwrap()
        else await deletePlan({ matchId: id }).unwrap()
      } catch {
        failed.push(id)
      }
    }
    const ok = selectedIds.length - failed.length
    if (failed.length === 0) {
      snack.success(`Successfully Deleted (${ok} plan${ok > 1 ? 's' : ''})`)
    } else if (ok > 0) {
      snack.warning(`Deleted ${ok}; failed ${failed.length} (ID: ${failed.join(', ')}).`)
    } else {
      snack.error(`Delete failed for ${failed.length} plan(s).`)
    }
    setSelectedIds(failed)
    setConfirmDeleteOpen(false)
  }

  const popoverColumn = useMemo<Col | null>(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, rows) : 'text'),
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, rows],
  )

  const toggleSort = (key: string) => {
    setSortState((cur) => toggleTableSort(cur, key))
    setPage(0)
  }

  const minTableWidth = Math.max(
    800,
    visibleColumns.reduce((acc, c) => acc + (c.width ?? 120), 0),
  )
  const gridKey = `${schema}:${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  return (
    <>
      <DialogTitle
        component="div"
        sx={{
          pl: { xs: 1.25, sm: 2 },
          pr: { xs: 0.75, sm: 1 },
          py: { xs: 0.5, sm: 1 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          flexWrap: 'wrap',
        }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '0.82rem', sm: '0.95rem' },
            whiteSpace: 'nowrap',
          }}
          title="DATA PLAN"
        >
          DATA PLAN
        </Typography>
        <Chip
          size="small"
          label={`${rows.length.toLocaleString('en-US')} rows`}
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
        {rowsQuery.isFetching ? <CircularProgress size={12} sx={{ ml: 0.5 }} /> : null}
        {/* 3 nút mode lên cùng hàng tiêu đề (Access btnDirect/btnPStock/btnSStock).
            xs: label ẩn → button icon-only (xem ModeButton); ml giảm để 3 button
            đứng cùng row với title trên 1 dòng compact. */}
        <Stack direction="row" spacing={{ xs: 0.25, sm: 1 }} alignItems="center" useFlexGap flexWrap="wrap" sx={{ ml: { xs: 0.25, sm: 1 } }}>
          <ModeButton
            active={mode === 'direct'}
            onClick={() => changeMode('direct')}
            icon={<SwapHorizIcon fontSize="small" />}
            label={MODE_CONFIG.direct.label}
            color={MODE_CONFIG.direct.color}
          />
          <ModeButton
            active={mode === 'pstock'}
            onClick={() => changeMode('pstock')}
            icon={<ShoppingCartOutlinedIcon fontSize="small" />}
            label={MODE_CONFIG.pstock.label}
            color={MODE_CONFIG.pstock.color}
          />
          <ModeButton
            active={mode === 'sstock'}
            onClick={() => changeMode('sstock')}
            icon={<StorefrontOutlinedIcon fontSize="small" />}
            label={MODE_CONFIG.sstock.label}
            color={MODE_CONFIG.sstock.color}
          />
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Reload">
          <span>
            <IconButton size="small" onClick={handleReload} disabled={rowsQuery.isFetching}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {/* xs: IconButton thay Button có text — tiết kiệm chỗ trên title bar mobile.
            sm+: Button outlined với text "Close" cho rõ ràng action. */}
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          onClick={onClose}
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<CloseIcon fontSize="small" />}
          sx={{ ml: 0.5, display: { xs: 'none', sm: 'inline-flex' } }}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}
      >
        {/* Grid — dataList template (skill table-style): FilterBar + per-column ▽
            + TableVirtuoso + Columns menu. Schema cột phụ thuộc filter chọn.
            Report filter (Choose Filter type / date / grade) đặt ở rightSlot —
            cùng hàng với Filter Area, căn phải. */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <FilterBar
            hasActiveFilters={hasActiveFilters}
            onClearAll={clearAllFilters}
            activeFilterCount={activeFilterCount}
            rightSlot={
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{ justifyContent: 'flex-end' }}
              >
                <Autocomplete
                  size="small"
                  options={availableFilters}
                  value={filter}
                  onChange={(_e, v) => { if (v) handleFilter(v) }}
                  getOptionLabel={(o) => FILTER_OPTIONS[o]?.label ?? String(o)}
                  isOptionEqualToValue={(a, b) => a === b}
                  disableClearable
                  sx={{ minWidth: { xs: 140, sm: 210 } }}
                  renderInput={(params) => (
                    <TextField {...params} label="Choose Filter type"
                      InputLabelProps={{ shrink: true }}
                      sx={matchingFilterAutocompleteSx} />
                  )}
                />
                <DatePicker
                  label={opt.dateLabel}
                  format="DD-MMM-YYYY"
                  value={range.from ? dayjs(range.from) : null}
                  onChange={(d) =>
                    handleRange({ ...range, from: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      InputLabelProps: { shrink: true },
                      sx: matchingRangeDateFieldSx,
                      inputProps: { 'aria-label': `${opt.dateLabel} start date` },
                    },
                    openPickerButton: { sx: { p: { xs: 0, sm: 0.25 } } },
                    openPickerIcon: { sx: { fontSize: { xs: 13, sm: 16 } } },
                  }}
                />
                <ArrowRightAltIcon
                  sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.disabled', flexShrink: 0, mx: { xs: 0.1, sm: 0.25 } }}
                  aria-hidden="true"
                />
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={range.to ? dayjs(range.to) : null}
                  onChange={(d) =>
                    handleRange({ ...range, to: d && d.isValid() ? d.format('YYYY-MM-DD') : '' })
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: matchingRangeDateFieldSx,
                      inputProps: { 'aria-label': `${opt.dateLabel} end date` },
                    },
                    openPickerButton: { sx: { p: { xs: 0, sm: 0.25 } } },
                    openPickerIcon: { sx: { fontSize: { xs: 13, sm: 16 } } },
                  }}
                />
                <Autocomplete
                  size="small"
                  options={categoryOptions}
                  value={category || null}
                  onChange={(_e, v) => handleCategory(v ?? '')}
                  getOptionLabel={(o) => o || ''}
                  isOptionEqualToValue={(a, b) => a === b}
                  sx={{ minWidth: { xs: 92, sm: 140 } }}
                  renderInput={(params) => (
                    <TextField {...params} label={opt.gradeLabel}
                      placeholder="(all)"
                      InputLabelProps={{ shrink: true }}
                      sx={matchingFilterAutocompleteSx} />
                  )}
                />
              </Stack>
            }
          >
            <FilterButton
              label="Search"
              active={!!searchTerm.trim()}
              onClick={(e) => searchPopover.open('search', e)}
            />
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => openColMenu(e.currentTarget)}
              sx={filterBarBtnSx}
              title="Columns"
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>Columns</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
            </Button>
            {doEditableMode ? (
              <Tooltip
                title={
                  selectedIds.length === 0
                    ? 'Tick at least one row to use Edit vs DO.'
                    : selectedIds.length === 1
                      ? `Edit DOs of match ID: ${selectedIds[0]}`
                      : `Edit DOs of ${selectedIds.length} matches: ${selectedIds.join(', ')}`
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddBusinessOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={handleEditVsDo}
                    disabled={!canEdit || selectedIds.length === 0}
                    title="Edit vs DO"
                    sx={[
                      filterBarBtnSx as Exclude<SxProps<Theme>, readonly unknown[]>,
                      {
                        color: ACCENT_DO,
                        borderColor: alpha(ACCENT_DO, 0.5),
                        '&:hover': { borderColor: ACCENT_DO, bgcolor: alpha(ACCENT_DO, 0.06) },
                      },
                    ]}
                  >
                    <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit vs DO</Box>
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {deletableMode ? (
              <Tooltip
                title={
                  selectedIds.length === 0
                    ? 'Tick at least one row to Delete Plan.'
                    : `Delete ${selectedIds.length} plan(s): ${selectedIds.join(', ')}`
                }
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={
                      deleting ? (
                        <CircularProgress size={11} sx={{ color: 'inherit' }} />
                      ) : (
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={handleDelete}
                    disabled={
                      !canEdit ||
                      deleting ||
                      deletingStockS ||
                      deletingStockP ||
                      selectedIds.length === 0
                    }
                    title="Delete Plan"
                    sx={filterBarBtnSx}
                  >
                    <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete Plan</Box>
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </FilterBar>

          <Divider sx={{ mb: 0.5 }} />

          <Box className="app-dataframe" sx={{ flex: 1, minHeight: 0, height: 'auto' }}>
            <Box
              className="app-dataframe-toolbar"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                minHeight: 24,
                py: 0,
                flexWrap: 'wrap',
              }}
            >
              {(rowsQuery.isFetching || !hydrated) && <CircularProgress size={14} />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
              >
                Total: {filteredRows.length}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
              >
                Loaded: {rows.length}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={rowsPerPage === -1 ? 0 : page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={TABLE_PAGINATION_COMPACT}
              />
            </Box>

            <FilterPopover
              open={searchPopover.state !== null}
              anchorEl={searchPopover.state?.anchor ?? null}
              onClose={() => searchPopover.close()}
              maxWidth={320}
            >
              {searchPopover.isOpen('search') && (
                <SearchFilterContent
                  value={searchTerm}
                  onChange={(v) => {
                    setSearchTerm(v)
                    setPage(0)
                  }}
                  placeholder="Contract, supplier, buyer, grade..."
                  label="Search grid"
                  onClear={() => searchPopover.close()}
                />
              )}
            </FilterPopover>

            <ColumnFilterPopover
              open={colPopover !== null}
              anchorPosition={colPopover?.position ?? null}
              onClose={() => setColPopover(null)}
              label={popoverColumn?.label ?? ''}
              kind={popoverKind === 'none' ? 'text' : popoverKind}
              value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
              onChange={(v) => {
                if (!colPopover) return
                columnFilters.setFilter(String(colPopover.key), v)
                setPage(0)
              }}
              options={popoverOptions}
            />

            <Box sx={{ flex: 1, minHeight: 280, overflow: 'hidden' }}>
              {(rowsQuery.isFetching || !hydrated) && rows.length === 0 ? (
                <BrandState tone="loading" title="Loading…" description="Please wait." />
              ) : pagedRows.length === 0 ? (
                <BrandState
                  title="No rows"
                  description="No records for the chosen filter."
                />
              ) : (
                <TableVirtuoso
                  key={gridKey}
                  style={{ height: '100%' }}
                  data={pagedRows}
                  fixedHeaderContent={() => {
                    const pageMids = pagedRows
                      .map((r) => Number((r as Record<string, unknown>).matchId))
                      .filter((m) => Number.isFinite(m))
                    const pageAll =
                      pageMids.length > 0 && pageMids.every((m) => selectedIds.includes(m))
                    const pageSome = pageMids.some((m) => selectedIds.includes(m)) && !pageAll
                    const togglePage = () =>
                      setSelectedIds((prev) =>
                        pageAll
                          ? prev.filter((m) => !pageMids.includes(m))
                          : Array.from(new Set([...prev, ...pageMids])),
                      )
                    return (
                    <TableRow>
                      {deletableMode && (
                        <TableCell
                          className="app-table-header-cell"
                          padding="checkbox"
                          style={{ width: 44, minWidth: 44, maxWidth: 44 }}
                          sx={{ px: 0.5, fontSize: '0.7rem' }}
                        >
                          <Checkbox
                            size="small"
                            checked={pageAll}
                            indeterminate={pageSome}
                            disabled={pageMids.length === 0}
                            onChange={togglePage}
                            inputProps={{ 'aria-label': 'Select all rows on page' }}
                            sx={{ p: 0.25 }}
                          />
                        </TableCell>
                      )}
                      {visibleColumns.map((col) => {
                        const w = getWidth(String(col.key)) ?? col.width
                        return (
                          <TableCell
                            key={String(col.key)}
                            align={col.align}
                            className="app-table-header-cell"
                            onClick={() => toggleSort(String(col.key))}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{
                              whiteSpace: 'nowrap',
                              cursor: 'pointer',
                              // Cho phép bôi đen + copy tên cột (click-sort vẫn chạy).
                              userSelect: 'text',
                              position: 'relative',
                              fontSize: '0.7rem',
                              // Padding ngang gọn (default MUI 16px/cạnh ăn hết chỗ
                              // → header dài + icon ▽ bị cắt). 6px/cạnh đủ thoáng.
                              px: 0.75,
                            }}
                          >
                            {col.headerTip ? (
                              <Tooltip title={col.headerTip} arrow placement="top">
                                <Box
                                  component="span"
                                  sx={{ textDecoration: 'underline dotted', cursor: 'help' }}
                                >
                                  {col.label}
                                </Box>
                              </Tooltip>
                            ) : (
                              col.label
                            )}
                            {getTableSortSuffix(sortState, String(col.key))}
                            <ColumnFilterIcon
                              active={columnFilters.isActive(String(col.key))}
                              onClick={(position) =>
                                setColPopover({ key: String(col.key), position })
                              }
                              ariaLabel={`Filter ${col.label}`}
                            />
                            <ResizeHandle onMouseDown={(e) => startResize(String(col.key), e)} />
                          </TableCell>
                        )
                      })}
                    </TableRow>
                    )
                  }}
                  itemContent={(_, row: Record<string, unknown>) => {
                    const rowMid = Number(row.matchId)
                    const rowSelected = Number.isFinite(rowMid) && selectedIds.includes(rowMid)
                    return [
                      deletableMode ? (
                        <TableCell
                          key="__select__"
                          padding="checkbox"
                          style={{ width: 44, minWidth: 44, maxWidth: 44 }}
                          sx={{ px: 0.5 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={rowSelected}
                            disabled={!Number.isFinite(rowMid)}
                            onChange={() => toggleSelect(rowMid)}
                            inputProps={{ 'aria-label': `Select plan ID ${rowMid}` }}
                            sx={{ p: 0.25 }}
                          />
                        </TableCell>
                      ) : null,
                      ...visibleColumns.map((col) => {
                      const val = cellValue(row, col)
                      const w = getWidth(String(col.key)) ?? col.width
                      const wrap = getWidth(String(col.key)) != null
                      // CF Access: P/S Contract đỏ khi side tương ứng còn pending.
                      const pendingRed =
                        (col.key === 'pSide' && hasPending(row.pPlanPending)) ||
                        (col.key === 'sSide' && hasPending(row.sPlanPending))
                      // CF cột Pending: P/S Pending < 0 → đỏ + bold (acLessThan, value 0).
                      const pendingNeg =
                        (col.key === 'pPlanPending' || col.key === 'sPlanPending') &&
                        isNegPending(row[col.key])
                      if (wrap) {
                        return (
                          <TableCell
                            key={String(col.key)}
                            align={col.align}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{
                              py: 0.4,
                              px: 0.75,
                              fontSize: '0.7rem',
                              ...(pendingRed ? { color: '#ED1C24' } : null),
                              ...(pendingNeg && { color: '#ED1C24', fontWeight: 700 }),
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              verticalAlign: 'top',
                            }}
                          >
                            <Box component="span" className="app-table-cell-wrap">
                              {val || '-'}
                            </Box>
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell
                          key={String(col.key)}
                          align={col.align}
                          style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                          sx={{
                            whiteSpace: 'nowrap',
                            py: 0.4,
                            px: 0.75,
                            fontSize: '0.7rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            verticalAlign: 'top',
                            ...(pendingRed ? { color: '#ED1C24' } : null),
                            ...(pendingNeg && { color: '#ED1C24', fontWeight: 700 }),
                          }}
                        >
                          <Box
                            component="span"
                            className="app-table-cell-text"
                            title={val || '-'}
                            sx={{
                              display: 'inline-block',
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    }),
                    ]
                  }}
                  components={{
                    /* eslint-disable @typescript-eslint/no-explicit-any */
                    Table: (props: any) => (
                      <Table
                        {...props}
                        size="small"
                        stickyHeader
                        sx={{ minWidth: minTableWidth, width: '100%' }}
                      />
                    ),
                    TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                    TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                    TableRow: (props: any) => {
                      // Row-select cho mọi mode có Delete Plan (Edit vs DO + Delete Plan).
                      if (!deletableMode) return <TableRow {...props} hover />
                      const idx = Number(props['data-item-index'])
                      const row = Number.isFinite(idx) ? pagedRows[idx] : null
                      const mid = row ? Number((row as Record<string, unknown>).matchId) : null
                      const isSelected = mid != null && selectedIds.includes(mid)
                      return (
                        <TableRow
                          {...props}
                          hover
                          onClick={() => mid != null && toggleSelect(mid)}
                          sx={{
                            cursor: 'pointer',
                            ...(isSelected && {
                              '& .MuiTableCell-root': {
                                bgcolor: (t: any) => `${alpha(t.palette.primary.main, 0.16)} !important`,
                                fontWeight: 700,
                              },
                            }),
                          }}
                        />
                      )
                    },
                    /* eslint-enable @typescript-eslint/no-explicit-any */
                  }}
                />
              )}
            </Box>
          </Box>

          <FuturesColumnsMenu controller={columnsCtrl} />
        </Box>
      </DialogContent>

      <CreateDoFromPlanDialog
        open={doEditOpen}
        scenario={editScenario}
        mode="edit"
        seeds={doEditSeeds}
        editRows={doEditRows}
        onClose={() => setDoEditOpen(false)}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Delete Plan"
        message={
          selectedIds.length === 0
            ? ''
            : planDos.length > 0
              ? `Deleting ${selectedIds.length} plan(s) (ID: ${selectedIds.join(
                  ', ',
                )}) will also delete ${planDos.length} booked DO: ${planDos
                  .map((d) => d.doNo || `#${d.deliveryOrderScsId}`)
                  .join(', ')}. Continue?`
              : `Do you want to delete ${selectedIds.length} selected plan(s) (ID: ${selectedIds.join(
                  ', ',
                )})?`
        }
        confirmLabel={planDos.length > 0 ? 'Delete DO(s) + Plan' : 'Delete Plan'}
        danger
        loading={deleting || deletingStockS || deletingStockP || deletingDos}
        onConfirm={confirmDelete}
        onClose={() => setConfirmDeleteOpen(false)}
      />
    </>
  )
}

/* ---------- mode button --------------------------------------------------- */

function ModeButton({
  active,
  onClick,
  icon,
  label,
  color,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
  color: string
}) {
  return (
    <Button
      variant={active ? 'contained' : 'outlined'}
      size="small"
      startIcon={icon}
      onClick={onClick}
      title={label}
      sx={{
        textTransform: 'none',
        fontWeight: 700,
        fontSize: '0.75rem',
        py: 0.4,
        minWidth: { xs: 0, sm: 64 },
        px: { xs: 0.5, sm: 1 },
        '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
        ...(active
          ? { bgcolor: color, color: '#fff', '&:hover': { bgcolor: color } }
          : { color, borderColor: alpha(color, 0.5) }),
      }}
    >
      <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{label}</Box>
    </Button>
  )
}
